import { useUserIdentity } from '@src/stores-v2';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import InfoIconAlt from '@src/svgs/InfoIconAlt';
import ClipsCameraIcon from '@src/svgs/quick-actions/VideoIcon';
import { isCompressedRenditionPending } from '@src/utility/storyReadiness';
import { getThemeTooltip } from '@src/utility/themeTooltips';
import { buildTopicUpdatedText } from '@src/utility/topicPrefixUtils';
import { useFlagsCombination } from '@src/utility/useFlagsCombination';
import { useRef, useState } from 'react';
import { Tooltip } from 'react-tooltip';
import styled, { css } from 'styled-components';

import AIClipProducerResults from '../../ClipProducer/AIClipProducerResults';
import AIClipProducerResultsSkeleton from '../../ClipProducer/AIClipProducerResultsSkeleton';
import ClipDurationButtonList from '../../ClipProducer/ClipDurationButtonList';
import AudienceFilterComponent from '../../common/AudienceFilterComponent';
import AutoResizeableMultilineInput from '../../common/AutoResizeableMultilineInput';
import GoalFilterComponent from '../../common/GoalFilterComponent';
import Modal from '../../common/Modal';
import SpeakerFilterComponent from '../../common/SpeakerFilterComponent';
import Switch from '../../common/Switch';
import TopicFilterDropdown from '../../common/TopicFilterDropdown';
import { VideoTemplateFilterComponent } from '../../common/VideoTemplateFilterComponent';
import { VideoTemplatesProvider } from '../../sidepanel/VideoTemplateSettings/VideoTemplatesContext';

import type { ClipProducerUIProps } from '../../ClipProducer/AIClipProducer';

function ClipProducerModalUI(
  props: ClipProducerUIProps & { closeModal: () => void },
) {
  const {
    supportedPresets,
    isProducing,
    retryCount,
    onProductionCancel,
    selectedDuration,
    setSelectedDuration,
    selectedPresets,
    setSelectedPresets,
    onPresetClick,
    customPresetText,
    setCustomPresetText,
    producedClips,
    showModal,
    onProduceButtonClick,
    closeProducingModal,
    // Suggested topics props
    suggestedThemes,
    selectedSuggestedTheme,
    onSuggestedThemeClick,
    // New speaker selection props
    speakers,
    selectedSpeakers,
    setSelectedSpeakers,
    useSpeakerFilter,
    setUseSpeakerFilter,
    isLoadingSpeakers,
    showSpeakerSelector,
    // Goal selection props
    selectedGoal,
    setSelectedGoal,
    useGoalFilter,
    setUseGoalFilter,
    // Audience selection props
    selectedAudiences,
    setSelectedAudiences,
    useAudienceFilter,
    setUseAudienceFilter,
    // Video Template selection
    selectedVideoTemplateId,
    setSelectedVideoTemplateId,
    useVideoTemplateFilter,
    setUseVideoTemplateFilter,
    // Topic filter selection
    storyTopics,
    selectedTopics,
    handleTopicsApply,
    // Feature flag for custom preset input
    enableCustomPreset,
  } = props;

  const [useCustomPrompt, setUseCustomPrompt] = useState(false);
  const videoCreator = useVideoCreatorStore();
  const userIdentity = useUserIdentity();
  const { enableClipProducerVideoTemplateSelection } = useFlagsCombination(
    userIdentity.currentRole,
  );
  const goals = videoCreator.organization?.branding?.goals || [];
  const audiences = videoCreator.organization?.branding?.audiences || [];

  // Validation state
  const [themeError, setThemeError] = useState<string | null>(null);
  const themeSectionRef = useRef<HTMLDivElement>(null);

  const handleProduceClick = () => {
    if (isProducing) return;
    // A prompt selection is required. Applying Story Topics injects a
    // "Topics: ..." prefix into customPresetText, so strip it before checking:
    // topics are a filter, not a prompt, and must not satisfy the requirement
    // on their own. In "Describe your own prompt" mode the remaining text is
    // the prompt; otherwise a preset or suggested topic must be selected.
    const promptTextWithoutTopics = buildTopicUpdatedText(
      selectedTopics,
      [],
      customPresetText,
    ).trim();
    const hasPrompt = useCustomPrompt
      ? Boolean(promptTextWithoutTopics)
      : selectedPresets.length > 0 || Boolean(selectedSuggestedTheme);
    if (!hasPrompt) {
      setThemeError(
        useCustomPrompt
          ? 'Please describe your prompt to continue'
          : 'Please select a prompt to continue',
      );
      themeSectionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      return;
    }
    setThemeError(null);
    onProduceButtonClick();
  };

  const handlePresetClick = (preset: string) => {
    setThemeError(null);
    onPresetClick(preset);
  };

  const handleSuggestedThemeClick = (title: string) => {
    setThemeError(null);
    onSuggestedThemeClick(title);
  };

  const stopProducingAndClose = () => {
    closeProducingModal();
    props.closeModal();
  };

  const showResults = showModal && Boolean(producedClips?.length);
  // Generating, nothing back yet: the results modal's skeleton stands in for
  // the form, so the wait already has the shape of what is coming.
  const showSkeleton = isProducing && !showResults;

  return (
    <>
      {showSkeleton && (
        <Modal
          isOpen
          closeModal={stopProducingAndClose}
          contentStyle={{
            marginTop: '70px',
            marginBottom: '70px',
          }}
        >
          <AIClipProducerResultsSkeleton
            message={
              retryCount > 0
                ? `Generating clips… Retrying (${retryCount})`
                : 'Generating clips…'
            }
            aspectRatio={videoCreator.currentVideo?.aspectRatio}
            onCancel={retryCount > 0 ? onProductionCancel : undefined}
          />
        </Modal>
      )}
      {/* Hide the initial modal when results are being shown */}
      {!showResults && !showSkeleton && (
        <Modal
          isOpen
          closeModal={stopProducingAndClose}
          contentStyle={{
            marginTop: '70px',
            marginBottom: '70px',
          }}
        >
          <Wrapper data-testid="clip-producer-modal">
            <ScrollArea>
              <Content>
                <TopHeading>Let's produce some clips</TopHeading>
                <PresetsList ref={themeSectionRef} hasError={!!themeError}>
                  <ThemeHeaderRow>
                    {!useCustomPrompt && (
                      <Leading>
                        Select your prompt <RequiredMark>*</RequiredMark>
                      </Leading>
                    )}
                    {useCustomPrompt && (
                      <Leading>
                        Describe your own prompt <RequiredMark>*</RequiredMark>
                      </Leading>
                    )}
                    {storyTopics.length > 0 && (
                      <TopicFilterDropdown
                        topics={storyTopics}
                        selectedTopics={selectedTopics}
                        onApply={handleTopicsApply}
                      />
                    )}
                  </ThemeHeaderRow>
                  {!useCustomPrompt && (
                    <>
                      <div className="options-list">
                        {supportedPresets.map(preset => {
                          const isActive = selectedPresets.includes(
                            preset.title,
                          );
                          return (
                            <PresetOption
                              key={preset.title}
                              active={isActive}
                              onClick={() => {
                                setUseCustomPrompt(false);
                                setCustomPresetText(
                                  selectedTopics.length > 0
                                    ? `Topics: ${selectedTopics.join(', ')}`
                                    : '',
                                );
                                handlePresetClick(preset.title);
                              }}
                              data-testid={`theme-${preset.title.toLowerCase().replace(/\s+/g, '-')}`}
                            >
                              <PresetRadio active={isActive}>
                                {isActive && <PresetRadioDot />}
                              </PresetRadio>
                              <PresetLabel>
                                {preset.title}
                                <ThemeInfoIcon
                                  data-tooltip-id="theme-tooltip"
                                  data-tooltip-content={getThemeTooltip(
                                    preset.title,
                                    preset.userPromptDescription,
                                  )}
                                  onClick={e => e.stopPropagation()}
                                >
                                  <InfoIconAlt width="10" height="10" />
                                </ThemeInfoIcon>
                              </PresetLabel>
                            </PresetOption>
                          );
                        })}
                      </div>
                      {(selectedPresets.length > 0 ||
                        selectedTopics.length > 0) && (
                        <CustomizeThemeSection>
                          <CustomizeThemeLabel>
                            Customize your prompt
                          </CustomizeThemeLabel>
                          <TextareaHoverWrapper>
                            <AutoResizeableMultilineInput
                              disableAutoResize
                              width="100%"
                              fontSize="12px"
                              placeholder={
                                selectedPresets[0]
                                  ? `Refine "${selectedPresets[0]}" (e.g., 'Moments where the speaker discusses overcoming key challenges')`
                                  : `Add additional context (e.g., 'Moments where the speaker discusses overcoming key challenges')`
                              }
                              value={customPresetText}
                              getValue={(value: string) => {
                                setCustomPresetText(value);
                              }}
                              customStyles={
                                customPresetText
                                  ? { borderColor: 'var(--accent)' }
                                  : undefined
                              }
                            />
                          </TextareaHoverWrapper>
                        </CustomizeThemeSection>
                      )}
                    </>
                  )}
                  {enableCustomPreset && (
                    <CustomPromptSection
                      data-testid="clip-producer-custom-prompt"
                      onClick={() => {
                        const next = !useCustomPrompt;
                        setUseCustomPrompt(next);
                        if (next) {
                          setSelectedPresets([]);
                          setCustomPresetText('');
                        } else {
                          setCustomPresetText('');
                        }
                      }}
                    >
                      <CustomPromptHeader>
                        <CustomPromptLabel>
                          <CustomPromptIcon>✦</CustomPromptIcon>
                          <CustomPromptText>
                            <CustomPromptTitle>
                              Describe your own prompt
                              {useCustomPrompt && (
                                <RequiredMark>*</RequiredMark>
                              )}
                            </CustomPromptTitle>
                          </CustomPromptText>
                        </CustomPromptLabel>
                        <Switch
                          label=""
                          checked={useCustomPrompt}
                          onClick={e => {
                            e.stopPropagation();
                            const next = !useCustomPrompt;
                            setUseCustomPrompt(next);
                            if (next) {
                              setSelectedPresets([]);
                              setCustomPresetText('');
                            } else {
                              setCustomPresetText('');
                            }
                          }}
                          small
                        />
                      </CustomPromptHeader>
                      {useCustomPrompt && (
                        <PresentTextArea
                          id="customPresetInput"
                          onClick={e => e.stopPropagation()}
                          placeholder="Type in what you'd like to see (e.g., 'Moments where speaker discusses leadership challenges')"
                          value={customPresetText}
                          onChange={e => {
                            setCustomPresetText(e.target.value);
                            if (e.target.value) setThemeError(null);
                          }}
                          autoFocus
                        />
                      )}
                    </CustomPromptSection>
                  )}
                  {themeError && (
                    <ValidationErrorMessage>
                      {themeError}
                    </ValidationErrorMessage>
                  )}
                </PresetsList>

                {suggestedThemes?.length > 0 && (
                  <PresetsList>
                    <Leading>Suggested topics</Leading>
                    <div className="options-list">
                      {suggestedThemes.map(title => (
                        <PresetOption
                          key={title}
                          active={selectedSuggestedTheme === title}
                          onClick={() => handleSuggestedThemeClick(title)}
                        >
                          {title}
                        </PresetOption>
                      ))}
                    </div>
                  </PresetsList>
                )}

                <FilterSection>
                  <DurationFilter>
                    <Leading>
                      Choose your desired approximate length in seconds
                    </Leading>
                    <ClipDurationButtonList
                      setSelectedDuration={setSelectedDuration}
                      selectedDuration={selectedDuration}
                    />
                  </DurationFilter>
                </FilterSection>

                <CompactSections>
                  {/* Goal selection */}
                  {goals.length > 0 && (
                    <GoalFilterComponent
                      align="left"
                      goals={goals}
                      selectedGoal={selectedGoal}
                      setSelectedGoal={setSelectedGoal}
                      useGoalFilter={useGoalFilter}
                      setUseGoalFilter={setUseGoalFilter}
                      allowNone
                    />
                  )}

                  {/* Audience selection */}
                  {audiences.length > 0 && (
                    <AudienceFilterComponent
                      align="left"
                      audiences={audiences}
                      selectedAudiences={selectedAudiences}
                      setSelectedAudiences={setSelectedAudiences}
                      useAudienceFilter={useAudienceFilter}
                      setUseAudienceFilter={setUseAudienceFilter}
                    />
                  )}

                  {/* Speaker selection UI - only show if enabled */}
                  {showSpeakerSelector && (
                    <SpeakerFilterComponent
                      align="left"
                      speakers={speakers}
                      selectedSpeakers={selectedSpeakers}
                      setSelectedSpeakers={setSelectedSpeakers}
                      useSpeakerFilter={useSpeakerFilter}
                      setUseSpeakerFilter={val =>
                        setUseSpeakerFilter(
                          typeof val === 'function'
                            ? val(useSpeakerFilter)
                            : val,
                        )
                      }
                      isLoadingSpeakers={isLoadingSpeakers}
                      title="Filter clips by speaker"
                    />
                  )}

                  {/* Video Template selection.
                      PLAT-11445 (audio-first): VideoTemplatesProvider rewrites
                      the editor timeline and calls setDuration to build template
                      previews; over a not-yet-processed audio-first video that
                      spins an infinite re-render loop (tab freeze). Templates
                      preview branding over the real video, which does not exist
                      until renditions land — hide the section until then. */}
                  {enableClipProducerVideoTemplateSelection &&
                    !isCompressedRenditionPending(videoCreator.story) && (
                      <VideoTemplatesProvider
                        videoCreator={videoCreator}
                        editorStore={videoCreator.rootStore.editorStore}
                      >
                        <VideoTemplateFilterComponent
                          useVideoTemplateFilter={useVideoTemplateFilter}
                          setUseVideoTemplateFilter={setUseVideoTemplateFilter}
                          selectedVideoTemplateId={selectedVideoTemplateId}
                          setSelectedVideoTemplateId={
                            setSelectedVideoTemplateId
                          }
                          align="left"
                        />
                      </VideoTemplatesProvider>
                    )}
                </CompactSections>
              </Content>
            </ScrollArea>

            <Tooltip
              id="theme-tooltip"
              noArrow
              opacity={1}
              style={{
                maxWidth: '240px',
                backgroundColor: 'var(--popover)',
                color: 'var(--popover-foreground)',
                fontSize: 'var(--text-xs)',
                fontWeight: 'var(--font-weight-medium)',
                border: '1px solid var(--border-subtle)',
                borderRadius: '6px',
                boxShadow: 'var(--elevation-sm)',
                padding: '5px 10px',
                zIndex: '1000003',
                lineHeight: '1.4',
              }}
              offset={8}
            />

            <Footer>
              <ProduceButton
                onClick={handleProduceClick}
                disabled={isProducing}
                data-testid="produce-button"
              >
                <ClipsCameraIcon width="16" height="16" /> Produce
              </ProduceButton>
            </Footer>
          </Wrapper>
        </Modal>
      )}
      {showResults && (
        <Modal
          isOpen
          closeModal={stopProducingAndClose}
          contentStyle={{
            marginTop: '70px',
            marginBottom: '70px',
          }}
        >
          <AIClipProducerResults
            sourcePlatform="content-studio"
            onClose={stopProducingAndClose}
            fragments={producedClips!}
            isProducing={isProducing}
            generationContext={{
              themes: selectedPresets,
              customPrompt: customPresetText || undefined,
              suggestedTheme: selectedSuggestedTheme,
              duration: selectedDuration,
              speakers: useSpeakerFilter
                ? selectedSpeakers.map(s => s.name || s.id)
                : undefined,
              goal: useGoalFilter ? selectedGoal?.label : undefined,
              audiences: useAudienceFilter
                ? selectedAudiences.map(a => a.label)
                : undefined,
            }}
          />
        </Modal>
      )}
    </>
  );
}

export default ClipProducerModalUI;

const Wrapper = styled.div`
  border-radius: 10px;
  background-color: var(--surface-subtle);
  width: 100%;
  max-width: 942px;
  min-width: 825px;
  border: 1px solid var(--border);
  max-height: calc(100vh - 80px);
  display: flex;
  flex-direction: column;
`;

const ScrollArea = styled.div`
  flex: 1;
  overflow-y: auto;
  overscroll-behavior: contain;
  scrollbar-width: thin;
  min-height: 0;
`;

const Content = styled.div`
  max-width: 580px;
  width: 100%;
  margin: 0 auto;
  display: grid;
  place-items: center;
  gap: 28px;
  padding: 0 24px 48px;
`;

const Footer = styled.div`
  padding: 24px 0 16px;
  display: flex;
  justify-content: center;
  flex-shrink: 0;
  position: relative;

  /* Gradient fade above the button to indicate scrollability */
  &::before {
    content: '';
    position: absolute;
    top: -40px;
    left: 0;
    right: 0;
    height: 40px;
    background: linear-gradient(
      to bottom,
      transparent 0%,
      var(--surface-subtle) 100%
    );
    pointer-events: none;
  }
`;

const TopHeading = styled.h2`
  font-weight: 700;
  font-size: 32px;
  line-height: 38.73px;
  text-align: center;
  color: var(--foreground);
  margin: 0;
  padding-top: 40px;
  padding-bottom: 24px;
`;

const Leading = styled.div<{ color?: string }>`
  width: 100%;
  color: ${props => props.color ?? 'var(--foreground)'};
  font-size: 12px;
  font-weight: 500;
  line-height: 150%;
  text-align: left;
`;

const PresentTextArea = styled.textarea`
  box-sizing: border-box;
  display: flex;
  padding: 12px;
  flex-direction: column;
  align-items: flex-start;
  border-radius: 8px;
  border: 1px solid var(--border);
  font-family: 'Inter', sans-serif;
  text-align: left;
  font-size: 12px;
  line-height: 150%;
  background-color: var(--surface-subtle);
  color: var(--foreground);
  width: 100%;
  resize: none;
  height: 70px;
  outline: none;
  &::placeholder {
    color: var(--text-muted);
  }
`;

const TextareaHoverWrapper = styled.div`
  textarea {
    transition: border-color 150ms ease;
    color: var(--foreground);
    background-color: var(--surface);
    border-radius: 8px;
  }
`;

const CustomizeThemeSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const CustomizeThemeLabel = styled.div`
  color: var(--foreground);
  font-size: 12px;
  font-weight: 500;
  line-height: 140%;
  width: 100%;
  text-align: left;
`;

const CustomPromptSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 12px;
  border-radius: 8px;
  border: 1px solid var(--border);
  background-color: var(--surface);
  box-sizing: border-box;
  cursor: pointer;
  transition: 150ms;
`;

const CustomPromptHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`;

const CustomPromptLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const CustomPromptIcon = styled.span`
  color: var(--primary);
  font-size: 14px;
  flex-shrink: 0;
`;

const CustomPromptText = styled.div`
  display: flex;
  flex-direction: column;
`;

const CustomPromptTitle = styled.span`
  color: var(--foreground);
  font-size: 12px;
  font-weight: 600;
  line-height: 140%;
`;

const DurationFilter = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 8px;
`;

const CompactSections = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`;

const FilterSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ProduceButton = styled.div.attrs((props: { disabled: boolean }) => props)`
  width: 240px;
  display: flex;
  height: 48px;
  padding: 16px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  border-radius: 8px;
  background: var(--primary);
  box-sizing: border-box;

  color: var(--background);
  font-size: 14px;
  font-weight: 700;
  line-height: normal;
  cursor: pointer;

  ${props =>
    props.disabled &&
    `
    cursor: not-allowed;
    color: var(--background);
    opacity: 0.6;
  `}
`;

const PresetsList = styled.div<{ hasError?: boolean }>`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin-bottom: 8px;

  .options-list {
    display: flex;
    width: 100%;
    flex-wrap: wrap;
    flex: 1;
    gap: 8px;
  }

  .input {
    width: 100%;
    display: flex;
    flex-direction: column;
    gap: 5px;
    label {
      font-size: 10px;
      color: var(--text-secondary);
    }
  }
  gap: 10px;
  ${({ hasError }) =>
    hasError &&
    css`
      border: 1px solid var(--destructive);
      border-radius: 8px;
      padding: 12px;
    `}
`;

const RequiredMark = styled.span`
  color: var(--destructive);
  margin-left: 2px;
`;

const ValidationErrorMessage = styled.div`
  color: var(--destructive);
  padding: 8px 12px;
  background-color: rgba(255, 107, 107, 0.1);
  border-radius: 8px;
  font-size: 12px;
  width: 100%;
  box-sizing: border-box;
`;

const PresetOption = styled.div.attrs((props: { active: boolean }) => props)`
  padding: 8px 12px;
  border-radius: 8px;
  width: calc(50% - 4px);
  box-sizing: border-box;
  border: 1px solid var(--border);
  background-color: var(--surface);
  color: var(--foreground);
  cursor: pointer;
  transition: all 0.15s ease;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover ${() => ThemeInfoIcon} {
    opacity: 1;
  }

  &:hover {
    background-color: var(--surface-elevated);
    border-color: var(--border);
  }
`;

const PresetLabel = styled.span`
  color: inherit;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.3;
  flex: 1;
`;

const ThemeInfoIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--text-muted);
  cursor: pointer;
  flex-shrink: 0;
  margin-left: 4px;
  vertical-align: middle;
  opacity: 0;
  transition:
    opacity 0.15s ease,
    color 0.15s ease;

  &:hover {
    color: inherit;
  }
`;

const ThemeHeaderRow = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const PresetRadio = styled.div<{ active: boolean }>`
  width: 14px;
  height: 14px;
  border-radius: 14px;
  border: 1px solid
    ${props => (props.active ? 'var(--accent)' : 'var(--text-muted)')};
  background: ${props => (props.active ? 'var(--background)' : 'transparent')};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const PresetRadioDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--accent);
`;
